<template>
  <header class="landing-hero">
    <!-- motion-blur filter for the spinning reel (referenced by .is-spinning) -->
    <svg width="0" height="0" aria-hidden="true" focusable="false" class="hero-blur-def">
      <defs>
        <filter id="reelMotionBlur" x="-10%" y="-70%" width="120%" height="240%" color-interpolation-filters="sRGB">
          <feGaussianBlur ref="blurG" in="SourceGraphic" stdDeviation="0 0" />
        </filter>
      </defs>
    </svg>

    <v-container class="landing-hero__inner pa-0">
      <div class="live-badge">
        <v-icon icon="mdi-star-four-points" size="13" />
        <span>{{ badge }}</span>
      </div>

      <h1
        ref="slot"
        class="hero-headline hero-slot"
        :class="{ 'hero-slot--spinning': spinning }"
        tabindex="0"
        role="button"
        title="Shuffle titles"
        :aria-label="headlines[current].plain"
        @click="spin"
        @keydown.enter.prevent="spin"
        @keydown.space.prevent="spin"
      >
        <span ref="reel" class="hero-reel" :class="{ 'is-spinning': spinning }" aria-hidden="true">
          <span v-for="(h, k) in seq" :key="k" class="hero-line">
            <!-- Static, author-controlled marketing copy (no user input) -->
            <!-- eslint-disable-next-line vue/no-v-html -->
            <span class="hl-inner" v-html="headlines[h].html"></span>
          </span>
        </span>
      </h1>

      <p class="hero-sub text-medium-emphasis">{{ sub }}</p>

      <div class="hero-ctas">
        <v-btn
          color="msq-yellow"
          variant="flat"
          height="48"
          class="hero-btn px-6"
          append-icon="mdi-arrow-right"
          @click="$emit('primary')"
        >
          Start building
        </v-btn>
        <v-btn
          color="primary"
          variant="outlined"
          height="48"
          class="hero-btn px-6"
          append-icon="mdi-arrow-right"
          @click="$emit('secondary')"
        >
          Watch it build a squad
        </v-btn>
        <span class="hero-meta text-disabled">No card. No call.</span>
      </div>
    </v-container>
  </header>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { rotatingHeadlines, heroBadge, heroSub, RotatingHeadline } from './landingContent'

const SPIN_FILLERS = 22
const SPIN_DURATION_MS = 2700
const SPIN_FALLBACK_MS = 3200

@Options({
  emits: ['primary', 'secondary']
})
export default class LandingHero extends Vue {
  badge = heroBadge
  sub = heroSub
  headlines: RotatingHeadline[] = rotatingHeadlines

  /** index of the headline currently at rest */
  current = 0
  /** headline indexes rendered in the reel strip (single item when at rest) */
  seq: number[] = [0]
  spinning = false

  private reduceMotion = false
  private blurTimer: number | null = null
  private fallbackTimer: number | null = null
  private startTimer: number | null = null
  private onTransitionEnd: (() => void) | null = null

  created() {
    this.current = Math.floor(Math.random() * this.headlines.length)
    this.seq = [this.current]
    try {
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    } catch {
      this.reduceMotion = false
    }
  }

  mounted() {
    // one spin shortly after load, matching the design behavior
    this.startTimer = window.setTimeout(() => this.spin(), 400)
  }

  beforeUnmount() {
    if (this.startTimer != null) window.clearTimeout(this.startTimer)
    this.stopSpinTimers()
  }

  private stopSpinTimers() {
    if (this.blurTimer != null) {
      window.clearInterval(this.blurTimer)
      this.blurTimer = null
    }
    if (this.fallbackTimer != null) {
      window.clearTimeout(this.fallbackTimer)
      this.fallbackTimer = null
    }
    const reel = this.$refs.reel as HTMLElement | undefined
    if (reel && this.onTransitionEnd) {
      reel.removeEventListener('transitionend', this.onTransitionEnd)
      this.onTransitionEnd = null
    }
  }

  private pickOther(): number {
    const n = Math.floor(Math.random() * (this.headlines.length - 1))
    return n >= this.current ? n + 1 : n
  }

  private setStatic(i: number) {
    const reel = this.$refs.reel as HTMLElement | undefined
    if (reel) {
      reel.style.transition = 'none'
      reel.style.transform = 'translateY(0)'
    }
    this.seq = [i]
    this.current = i
  }

  async spin() {
    if (this.spinning) return
    const landing = this.pickOther()
    if (this.reduceMotion) {
      this.setStatic(landing)
      return
    }
    const slot = this.$refs.slot as HTMLElement | undefined
    const reel = this.$refs.reel as HTMLElement | undefined
    if (!slot || !reel) {
      this.setStatic(landing)
      return
    }
    this.spinning = true
    // strip runs landing → fillers → current, and we scroll DOWN into it
    const strip: number[] = [landing]
    for (let k = 0; k < SPIN_FILLERS; k++) strip.push(Math.floor(Math.random() * this.headlines.length))
    strip.push(this.current)
    this.seq = strip
    await this.$nextTick()

    const lineH = slot.getBoundingClientRect().height
    const target = (strip.length - 1) * lineH
    reel.style.transition = 'none'
    reel.style.transform = `translateY(${-target}px)` // start on current (bottom of strip)
    void reel.offsetHeight // commit the start position before we animate

    // motion blur tracks the reel's actual velocity, easing to sharp as it settles
    const blurG = this.$refs.blurG as SVGElement | undefined
    let lastY = -target
    if (blurG) {
      blurG.setAttribute('stdDeviation', '0 0')
      this.blurTimer = window.setInterval(() => {
        const m = getComputedStyle(reel).transform
        let y = 0
        if (m && m.indexOf('matrix') === 0) {
          const p = m.slice(m.indexOf('(') + 1, -1).split(',')
          y = parseFloat(p[5]) || 0
        }
        const v = Math.abs(y - lastY)
        lastY = y
        blurG.setAttribute('stdDeviation', `0 ${Math.min(16, v * 0.4).toFixed(2)}`)
      }, 25)
    }

    reel.style.transition = `transform ${SPIN_DURATION_MS / 1000}s cubic-bezier(0.11, 0.78, 0.16, 1)`
    reel.style.transform = 'translateY(0)' // scroll down onto the landing title (top of strip)

    let settled = false
    const done = () => {
      if (settled) return
      settled = true
      this.stopSpinTimers()
      if (blurG) blurG.setAttribute('stdDeviation', '0 0')
      this.spinning = false
      this.setStatic(landing) // collapse to the resting item (seamless — landing already shown)
    }
    this.onTransitionEnd = done
    reel.addEventListener('transitionend', done)
    this.fallbackTimer = window.setTimeout(done, SPIN_FALLBACK_MS) // fallback if transitionend never fires
  }
}
</script>

<style scoped lang="scss">
.landing-hero {
  padding: 96px 24px 64px;
}
.landing-hero__inner {
  max-width: 1280px;
  margin: 0 auto;
}
.hero-blur-def {
  position: absolute;
  width: 0;
  height: 0;
  pointer-events: none;
}
.live-badge {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: var(--l-mono);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgb(var(--v-theme-primary));
  border: 1px solid var(--l-border);
  border-radius: 9999px;
  padding: 6px 14px;
  margin-bottom: 32px;
}
.v-theme--light .live-badge {
  color: rgb(var(--v-theme-on-background));
}
.hero-headline.hero-slot {
  font-size: clamp(22px, 5.4vw, 56px);
  line-height: 1.08;
  font-weight: 700;
  letter-spacing: -0.025em;
  max-width: 100%;
  width: fit-content;
  height: 2.5em;
  overflow: hidden;
  position: relative;
  margin: 0 0 30px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  outline: none;
}
.hero-headline.hero-slot:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 8px;
  border-radius: 4px;
}
/* feathered top/bottom edges while the reel spins, so lines fade past the
   window instead of hard-clipping; fades out at rest to keep the title crisp */
.hero-headline.hero-slot::before,
.hero-headline.hero-slot::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  height: 0.5em;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.25s ease;
  z-index: 1;
}
.hero-headline.hero-slot::before {
  top: 0;
  background: linear-gradient(to bottom, rgb(var(--v-theme-background)), rgba(var(--v-theme-background), 0));
}
.hero-headline.hero-slot::after {
  bottom: 0;
  background: linear-gradient(to top, rgb(var(--v-theme-background)), rgba(var(--v-theme-background), 0));
}
.hero-headline.hero-slot.hero-slot--spinning::before,
.hero-headline.hero-slot.hero-slot--spinning::after {
  opacity: 1;
}
.hero-reel {
  display: flex;
  flex-direction: column;
}
.hero-reel.is-spinning {
  filter: url(#reelMotionBlur);
  will-change: transform, filter;
}
.hero-line {
  height: 2.5em;
  overflow: hidden;
  display: flex;
  align-items: center;
}
.hl-inner {
  display: block;
  white-space: nowrap;
}
.hero-sub {
  font-size: 18px;
  line-height: 1.55;
  max-width: 64ch;
  margin: 0 0 40px;
  text-wrap: pretty;
}
.hero-ctas {
  display: flex;
  gap: 14px;
  align-items: center;
  flex-wrap: wrap;
}
.hero-btn {
  letter-spacing: 0.06em;
  border-radius: 4px;
}
.hero-meta {
  font-size: 13px;
  margin-left: 4px;
}
@media (max-width: 900px) {
  .landing-hero {
    padding: 64px 20px 48px;
  }
}
</style>
